import { cx } from '@shared/lib/cx'
import type { PixelProvider } from '@shared/contracts/pixels'
import {
  chipNote,
  dualMark,
  emptyMark,
  eventChip,
  stage,
  stageChips,
  stageLabel,
  stageMuted,
  timeline,
  timelineWrap,
} from './pixel-events-timeline.css'

export const DUAL_TRACK_HINT =
  'Enviado duas vezes e deduplicado: se o comprador fechar a aba do PIX, o servidor confirma mesmo assim.'

export const GOOGLE_BROWSER_ONLY_NOTE = 'só no navegador: padrão do mercado'

export const ABANDONED_CART_SERVER_NOTE = 'servidor: após 30 min sem atividade'

type TimelineChip = {
  label: string
  dual?: boolean
  note?: string
}

type TimelineStage = {
  label: string
  chips: TimelineChip[]
  muted?: boolean
}

function providerStages(provider: PixelProvider, googleBeginCheckout: boolean): TimelineStage[] {
  if (provider === 'facebook') {
    return [
      { label: 'Abriu o checkout', chips: [{ label: 'PageView' }, { label: 'InitiateCheckout' }] },
      { label: 'Preencheu pagamento', chips: [{ label: 'AddPaymentInfo' }] },
      { label: 'Gerou PIX', chips: [{ label: 'GeneratePix' }] },
      {
        label: 'Abandonou o checkout',
        chips: [{ label: 'AbandonedCart', note: ABANDONED_CART_SERVER_NOTE }],
      },
      { label: 'Pagou', chips: [{ label: 'Purchase', dual: true }] },
    ]
  }
  if (provider === 'tiktok') {
    return [
      {
        label: 'Abriu o checkout',
        chips: [{ label: 'ViewContent' }, { label: 'InitiateCheckout' }],
      },
      { label: 'Preencheu pagamento', chips: [{ label: 'AddPaymentInfo' }] },
      { label: 'Gerou PIX', chips: [] },
      {
        label: 'Abandonou o checkout',
        chips: [{ label: 'AbandonedCart', note: ABANDONED_CART_SERVER_NOTE }],
      },
      { label: 'Pagou', chips: [{ label: 'Purchase', dual: true }] },
    ]
  }
  if (provider === 'google_ads') {
    return [
      {
        label: 'Abriu o checkout',
        chips: googleBeginCheckout ? [{ label: 'Visita ao checkout' }] : [],
      },
      { label: 'Preencheu pagamento', chips: [] },
      { label: 'Gerou PIX', chips: [] },
      { label: 'Pagou', chips: [{ label: 'Conversão', note: GOOGLE_BROWSER_ONLY_NOTE }] },
    ]
  }
  return [
    { label: 'Abriu o checkout', chips: [] },
    { label: 'Preencheu pagamento', chips: [] },
    { label: 'Gerou PIX', chips: [{ label: 'Aguardando pagamento' }] },
    { label: 'Pagou', chips: [{ label: 'Pago' }] },
    { label: 'Depois', chips: [{ label: 'Reembolso' }, { label: 'Chargeback' }], muted: true },
  ]
}

export type PixelEventsTimelineProps = {
  provider: PixelProvider
  googleBeginCheckout?: boolean
}

export function PixelEventsTimeline({
  provider,
  googleBeginCheckout = false,
}: PixelEventsTimelineProps) {
  const stages = providerStages(provider, googleBeginCheckout)
  return (
    <div className={timelineWrap}>
      <div className={timeline} role="list" aria-label="Eventos enviados por este provedor">
        {stages.map((item) => (
          <div key={item.label} role="listitem" className={cx(stage, item.muted && stageMuted)}>
            <span className={stageLabel}>{item.label}</span>
            <div className={stageChips}>
              {item.chips.length === 0 ? (
                <span className={emptyMark} aria-label={`${item.label}: nenhum evento`}>
                  Não se aplica
                </span>
              ) : (
                item.chips.map((chip) => (
                  <span
                    key={chip.label}
                    className={eventChip}
                    title={chip.dual ? DUAL_TRACK_HINT : undefined}
                  >
                    {chip.label}
                    {chip.dual ? <span className={dualMark}>navegador + servidor</span> : null}
                  </span>
                ))
              )}
            </div>
            {item.chips
              .filter((chip) => chip.note)
              .map((chip) => (
                <span key={`${chip.label}-note`} className={chipNote}>
                  {chip.note}
                </span>
              ))}
          </div>
        ))}
      </div>
    </div>
  )
}
